import { useEffect, useState } from "react";
import {
    Avatar,
    Box,
    Container,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Stack,
    Typography,
} from "@mui/material";

import * as yup from "yup";
import { useFormik } from "formik";
import { useSelector } from "react-redux";

import { RootState, store } from "@store/store";
import {
    useDeleteCommentByAppMutation,
    useLazyGetCommentsByAppQuery,
    usePatchCommentByAppMutation,
    usePostCommentByAppMutation,
} from "@services/api/applications";

import SendIcon from "@mui/icons-material/Send";
import { ApplicationResume, Comment } from "@interfaces/application";
import { calculateDatetimeDiff } from "@helpers/generals";
import { CustomPopupModal } from "./CustomPopupModal";

import EditIcon from "@assets/edit.svg";
import TrashIicon from "@assets/trash_icon.svg";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { useLazyGetModulesByCodeQuery } from "@services/api/roles";
import { Permission } from "@interfaces/users";

interface LoanAppCommentsProps {
    listCode: string;
    data: ApplicationResume;
    // applicationType:
    //     | "Todos"
    //     | "Solicitudes"
    //     | "En Revisión"
    //     | "Perdieron interés"
    //     | "Rechazados"
    //     | "Autorizados"
    //     | "Clientes"
    //     | "Revisión Contractuales"
    //     | "Solicitud Target"
    //     | "Ofertadas"
    //     | "Ofertas Aceptadas"
    //     | "Ofertas Rechazadas";
}

export const LoanAppComments = (props: LoanAppCommentsProps) => {
    const [permission, setPermission] = useState<Permission>();
    const [editEnabled, setEditEnabled] = useState<boolean>();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [commentsData, setCommentsData] = useState<Array<Comment>>([]);
    const [commentOptionSelected, setCommentOptionSelected] = useState<Comment>();

    const userInfo = useSelector((state: RootState) => state.session.user);

    const permissions = (state = store.getState()) => state.session.permissions;

    const [triggerGetComments] = useLazyGetCommentsByAppQuery();
    const [triggerPostComment] = usePostCommentByAppMutation();
    const [triggerDeleteComment] = useDeleteCommentByAppMutation();
    const [triggerPatchComment] = usePatchCommentByAppMutation();
    const [triggerGetModulesByCode] = useLazyGetModulesByCodeQuery();

    const formik = useFormik({
        initialValues: {
            comment: "",
        },
        validationSchema: yup.object().shape({
            comment: yup.string().required("El campo no puede estar vacio"),
        }),
        onSubmit: async (values) => {
            try {
                // let moduleCode = "MSOL";
                // switch (props.applicationType) {
                //     case "Solicitudes":
                //         moduleCode = "MSOL";
                //         break;
                //     case "En Revisión":
                //         moduleCode = "MREV";
                //         break;
                //     case "Perdieron interés":
                //         moduleCode = "MPIN";
                //         break;
                //     case "Rechazados":
                //         moduleCode = "MREC";
                //         break;
                //     case "Autorizados":
                //         moduleCode = "MAUT";
                //         break;
                //     case "Clientes":
                //         moduleCode = "MCLI";
                //         break;
                // }

                let moduleCode = props.listCode;

                const module = await triggerGetModulesByCode(moduleCode).unwrap();

                await triggerPostComment({
                    applicationId: props.data.id ?? "0-0-0-0-0",
                    payload: {
                        comment: values.comment,
                        user_id: userInfo.id,
                        module_id: module?.id,
                    },
                }).unwrap();
            } catch (error) {
            } finally {
                formik.resetForm();
                await reloadComments();
            }
        },
    });

    const formikEdit = useFormik({
        initialValues: {
            commentEdit: "",
        },
        validationSchema: yup.object().shape({
            commentEdit: yup.string().required("El campo no puede estar vacio"),
        }),
        onSubmit: async (values) => {
            try {
                await triggerPatchComment({
                    applicationId: props.data.id ?? "0-0-0-0-0",
                    commentId: commentOptionSelected?.id ?? "0-0-0-0-0",
                    payload: {
                        comment: values.commentEdit,
                    },
                }).unwrap();
            } catch (error) {
            } finally {
                setEditEnabled(false);
                await reloadComments();
            }
        },
    });

    const handleOnCommentOptionsClicked = (event: React.SyntheticEvent<HTMLElement>, comment: Comment) => {
        setCommentOptionSelected(comment);
        setAnchorEl(event.currentTarget);
    };

    const handleOnClosePopup = () => {
        setAnchorEl(null);
    };

    const handleOnEditClicked = () => {
        setAnchorEl(null);
        setEditEnabled(true);

        formikEdit.setFieldValue("commentEdit", commentOptionSelected?.comment, true);
    };

    const handleOnDeleteClicked = async () => {
        await triggerDeleteComment({
            applicationId: props.data.id ?? "0-0-0-0-0",
            commentId: commentOptionSelected?.id ?? "0-0-0-0-0",
        });

        const comments = await triggerGetComments(props.data.id ?? "0-0-0-0-0");
        setCommentsData(comments.data ?? []);

        setAnchorEl(null);
    };

    const handleOnCancelClicked = () => {
        setAnchorEl(null);
        setEditEnabled(false);
    };

    const reloadComments = async () => {
        const permissionLocal = permissions(store.getState()).find((t) => t.module.code === "SBCOM");
        console.log(permissionLocal, permissionLocal?.can_watch);
        setPermission(permissionLocal);
        const comments = await triggerGetComments(props.data.id ?? "0-0-0-0-0").unwrap();
        setCommentsData(
            comments.filter((t) => ["MAUT", "MSOL", "MREV", "MREC", "MPIN", "MCLI"].some((a) => a === t.module.code)),
        );
    };

    useEffect(() => {
        if (props.data.id === "0-0-0-0-0" || props.data.id === undefined) return;
        reloadComments();
    }, [props.data.id]);

    return (
        <>
            <Container maxWidth="xs" sx={{ mb: 3 }}>
                <Stack spacing={3}>
                    {commentsData &&
                        commentsData.map((comment, index) => (
                            <Grid
                                key={index}
                                container
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center"
                                }}>
                                <Grid /*xs={1}*/ size={1}>
                                    <Avatar sx={{ bgcolor: "skyblue", width: 30, height: 30 }}>
                                        <Typography sx={{
                                            fontSize: 13
                                        }}>
                                            {comment.user.name.substring(0, 1) + comment.user.last_name.substring(0, 1)}
                                        </Typography>
                                    </Avatar>
                                </Grid>
                                <Grid size={6} sx={{
                                    pl: 1
                                }}>
                                    <Typography variant="body1" sx={{
                                        color: "#002652"
                                    }}>
                                        {comment.user.name.concat(
                                            " ",
                                            comment.user.last_name,
                                            " ",
                                            comment.user.last_name_2,
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid size={4}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "#818181",
                                            textAlign: "center"
                                        }}>
                                        {calculateDatetimeDiff(comment.created_at)}
                                    </Typography>
                                </Grid>
                                <Grid size={1}>
                                    <IconButton
                                        onClick={(event) => handleOnCommentOptionsClicked(event, comment)}
                                        disabled={permission?.can_edit ?? false === false}
                                    >
                                        <Typography
                                            sx={{
                                                color: "#528CD6",
                                                fontWeight: 600,
                                                fontSize: 20,
                                                textAlign: "right"
                                            }}>
                                            ...
                                        </Typography>
                                    </IconButton>
                                </Grid>
                                <Grid size={12} sx={{
                                    px: 1
                                }}>
                                    {editEnabled && commentOptionSelected?.id === comment.id ? (
                                        <form
                                            onSubmit={formikEdit.handleSubmit}
                                            noValidate
                                            hidden={permission?.can_edit ?? false === false}
                                        >
                                            <FormControl variant="outlined" fullWidth>
                                                <InputLabel htmlFor="commentEdit">Editar comentario</InputLabel>
                                                <OutlinedInput
                                                    id="commentEdit"
                                                    type="text"
                                                    label="Editar comentario"
                                                    onChange={formikEdit.handleChange}
                                                    onBlur={formikEdit.handleBlur}
                                                    value={formikEdit.values.commentEdit}
                                                    error={
                                                        formikEdit.touched.commentEdit &&
                                                        Boolean(formikEdit.errors.commentEdit)
                                                    }
                                                    endAdornment={
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                type="submit"
                                                                disabled={
                                                                    !formikEdit.dirty ||
                                                                    !formikEdit.isValid ||
                                                                    !(Object.keys(formikEdit.errors).length === 0)
                                                                }
                                                            >
                                                                <SendIcon />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    }
                                                />
                                            </FormControl>
                                        </form>
                                    ) : (
                                        <Typography variant="body1">{comment.comment}</Typography>
                                    )}
                                </Grid>
                                <Grid size={12}></Grid>
                            </Grid>
                        ))}
                    <form onSubmit={formik.handleSubmit} noValidate hidden={!(permission?.can_edit ?? true)}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel
                                htmlFor="comment"
                                error={formik.touched.comment && Boolean(formik.errors.comment)}
                            >
                                Agrega un comentario
                            </InputLabel>
                            <OutlinedInput
                                id="comment"
                                type="text"
                                label="Agrega un comentario"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.comment}
                                error={formik.touched.comment && Boolean(formik.errors.comment)}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            type="submit"
                                            disabled={
                                                !formik.dirty ||
                                                !formik.isValid ||
                                                !(Object.keys(formik.errors).length === 0)
                                            }
                                        >
                                            <SendIcon />
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />
                            {formik.touched.comment && Boolean(formik.errors.comment) && (
                                <FormHelperText>{formik.errors.comment}</FormHelperText>
                            )}
                        </FormControl>
                    </form>
                </Stack>
            </Container>
            <CustomPopupModal
                showClose={!editEnabled}
                anchorEl={anchorEl}
                children={
                    <Box
                        sx={{
                            mb: 2,
                            mx: editEnabled ? 1 : 0
                        }}>
                        <Grid container sx={{
                            textAlign: "center"
                        }}>
                            {editEnabled ? (
                                <Grid>
                                    <IconButton onClick={handleOnCancelClicked}>
                                        <CancelOutlinedIcon />
                                    </IconButton>
                                    <Typography>Cancelar</Typography>
                                </Grid>
                            ) : (
                                <>
                                    <Grid size={12}>
                                        <Typography>¿Que deseas hacer?</Typography>
                                    </Grid>
                                    <Grid size={6}>
                                        <IconButton onClick={handleOnEditClicked}>
                                            <img src={EditIcon} />
                                        </IconButton>
                                        <Typography>Editar</Typography>
                                    </Grid>
                                    <Grid size={6}>
                                        <IconButton onClick={handleOnDeleteClicked}>
                                            <img src={TrashIicon} />
                                        </IconButton>
                                        <Typography>Borrar</Typography>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Box>
                }
                onClose={handleOnClosePopup}
            />
        </>
    );
};
